import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { ModalController, Platform, IonSearchbar } from '@ionic/angular';
import { ConfigService } from '../../services/config/config.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GameTileService } from '../../services/game-tile/game-tile.service';
import { ControlService } from 'src/app/services/control/control.service';
import { GameHubControllerService } from 'src/app/services/gameHub/game-hub-controller';
import { NgClass } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { HighlightPipe } from 'src/app/pipes/highlight.pipe';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { FavoritesService } from 'src/app/services/favorite/favorite.service';
import { Navigator } from 'src/app/services/routing/navigator';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
  imports: [
    NgClass, HighlightPipe, IonicModule
  ]
})
export class SearchOverlayComponent implements OnInit, OnDestroy {

  @Input() allGamesCategoryData: any[]; // Assuming this is an array of game objects
  @ViewChild('searchbar', { static: false }) searchbar!: IonSearchbar;

  // New property to hold all dynamic tag categories
  private destroy$ = new Subject<void>();
  private gamesPageIndex: number = 0;
  private readonly GAMES_PER_PAGE: number = 20; // Number of games to load per batch
  private tagFilteredGamesHolder: any[] = []; // Holds games filtered by a tag, before text search
  public activeTextQuery: string = ''; // Holds the live text from the search bar
  public allCurrentlyFilteredGames: any[] = []; // Holds ALL games matching the current filter
  public currentFilterTerm: string = '';
  public filteredGames: any[] = []; // Holds the SUBSET of games currently displayed
  public isAuthenticated: boolean = false;
  public isIOS: boolean = false;
  public isLoadingTags: boolean = true;
  public isShowingFilteredGames: boolean = false;
  public isTextSearchActive: boolean = false; // True when text search is actively filtering
  public tagCategories: { categoryName: string, tags: any[] }[] = [];
  public showFavoriteOnSearchList:boolean;

  private vendorsThatSupportDemoPlay: { [key: string]: boolean } = {};



  constructor(
    private configService: ConfigService,
    private control: ControlService,
    private gameHubController: GameHubControllerService,
    private gameTileService: GameTileService,
    private modalController: ModalController,
    private platform: Platform,
    private utils: UtilsService,
    private userSession: UserSessionService,
    private favoritesService:FavoritesService,
    private navigator: Navigator,
  ) {
    this.isIOS = this.platform.is("ios");
  }

  ngOnInit() {
    this.showFavoriteOnSearchList = this.configService.getConfig().FEATURE_SHOW_FAVORITE_ON_SEARCH_LIST === "1" ? true : false;
    this.initializeDemoPlayConfig();

  if (this.allGamesCategoryData) {
    this.allGamesCategoryData = this.setDemoPlayFlag(this.allGamesCategoryData);
  }
    this.fetchTags();
  }

  private initializeDemoPlayConfig() {
    const featureDemoConfig = this.configService.getConfig()['FEATURE_VENDOR_DEMO'];
    if (featureDemoConfig) {
      try {
        const configVendors = JSON.parse(featureDemoConfig.replace(/'/g, '"'));
        this.vendorsThatSupportDemoPlay = configVendors;
      } catch (error) {
        console.error('Error parsing FEATURE_VENDOR_DEMO config:', error);
        this.vendorsThatSupportDemoPlay = {};
      }
    }
  }

  private setDemoPlayFlag(games: any) {
    if (Array.isArray(games)) {
      games.forEach(game => {
        const vendorIdent = game.vendor_ident?.toLowerCase();
        game.is_demoPlay = vendorIdent && vendorIdent in this.vendorsThatSupportDemoPlay
          ? this.vendorsThatSupportDemoPlay[vendorIdent]
          : false;
      });
    } else if (typeof games === 'object' && games !== null) {
      Object.values(games).forEach(categoryGames => {
        if (Array.isArray(categoryGames)) {
          categoryGames.forEach(game => {
            const vendorIdent = game.vendor_ident?.toLowerCase();
            game.is_demoPlay = vendorIdent && vendorIdent in this.vendorsThatSupportDemoPlay
              ? this.vendorsThatSupportDemoPlay[vendorIdent]
              : false;
          });
        }
      });
    }
    return games;
  }


  ionViewDidEnter() {
    // Auto-focus the search input when the component is loaded
    setTimeout(() => {
      if (this.searchbar) {
        this.searchbar.setFocus();
      }
    }, 150); // Small delay to ensure the DOM is fully rendered
  }

  fetchTags() {
    this.isLoadingTags = true;
    this.gameHubController.getLobbyTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.tagCategories = []; // Initialize

          const apiCategories = response?.payload;

          if (Array.isArray(apiCategories)) {

            const consolidatedCategories = new Map<string, { categoryName: string, tags: any[] }>();

            apiCategories.forEach(categoryObj => {
              if (categoryObj && categoryObj.category) {
                const categoryName = categoryObj.category;

                // Handle tags - if they exist and are an array, process them; otherwise use empty array
                const tagsArray = (categoryObj.tags && Array.isArray(categoryObj.tags)) ? categoryObj.tags : [];
                const processedTags = tagsArray.map(t => ({ id: t.id, name: t.tag_name, originalTag: t }));

                if (consolidatedCategories.has(categoryName)) {
                  // Category already exists, merge tags (ensure no duplicate tags within a category if API might send those too)
                  const existingCategory = consolidatedCategories.get(categoryName);
                  const existingTagIds = new Set(existingCategory.tags.map(t => t.id));
                  processedTags.forEach(newTag => {
                    if (!existingTagIds.has(newTag.id)) {
                      existingCategory.tags.push(newTag);
                      existingTagIds.add(newTag.id); // Add new tag id to set
                    }
                  });
                } else {
                  // New category
                  consolidatedCategories.set(categoryName, { categoryName: categoryName, tags: processedTags });
                }
              } else {
                console.warn('Skipping categoryObj due to missing category name:', categoryObj);
              }
            });

            this.tagCategories = Array.from(consolidatedCategories.values());

          } else {
            console.warn('response.payload is not an array or response.payload is missing:', apiCategories);
          }
          this.isLoadingTags = false;
        },
        (error) => {
          console.error('Error fetching tags:', error);
          this.isLoadingTags = false;
          this.tagCategories = []; // Ensure it's an empty array on error
        }
      );
  }

  filterGamesByTag(clickedTag: any) {
    if (!this.allGamesCategoryData || !clickedTag || !clickedTag.name) {
      console.warn('Cannot filter games: All Games data or clicked tag name is missing.');
      this.allCurrentlyFilteredGames = [];
      this.filteredGames = [];
      return;
    }
    this.currentFilterTerm = clickedTag.name; // This is the selected tag
    this.activeTextQuery = ''; // Clear any previous text search
    this.isTextSearchActive = false;

    const clickedTagNameLower = clickedTag.name.toLowerCase();
    this.tagFilteredGamesHolder = this.allGamesCategoryData.filter(game => {
      if (game && Array.isArray(game.tags)) {
        return game.tags.some(gameTag => typeof gameTag === 'string' && gameTag.toLowerCase() === clickedTagNameLower);
      }
      return false;
    });

    this.allCurrentlyFilteredGames = [...this.tagFilteredGamesHolder]; // Initially, show all tag-filtered games

    this.filteredGames = [];
    this.gamesPageIndex = 0;
    this.loadInitialGamesBatch();

    this.isShowingFilteredGames = true;
  }

  loadInitialGamesBatch() {
    const newGames = this.allCurrentlyFilteredGames.slice(
      this.gamesPageIndex * this.GAMES_PER_PAGE,
      (this.gamesPageIndex + 1) * this.GAMES_PER_PAGE
    );
    this.filteredGames.push(...newGames);
    this.gamesPageIndex++;
  }

  loadMoreGames(event: any) {
    if (this.filteredGames.length >= this.allCurrentlyFilteredGames.length) {
      event.target.complete();
      event.target.disabled = true;
      return;
    }

    // Simulate network latency if needed for testing
    // setTimeout(() => {
    const newGames = this.allCurrentlyFilteredGames.slice(
      this.gamesPageIndex * this.GAMES_PER_PAGE,
      (this.gamesPageIndex + 1) * this.GAMES_PER_PAGE
    );

    this.filteredGames.push(...newGames);
    this.gamesPageIndex++;

    event.target.complete();

    if (this.filteredGames.length >= this.allCurrentlyFilteredGames.length) {
      event.target.disabled = true;
    }
    // }, 500); // Simulate 0.5 second delay
  }

  // Method to go back to tag selection view
  showTagsView() {

    if (!this.isShowingFilteredGames) {
      this.dismissModal();
    } else {
      this.isShowingFilteredGames = false;
      this.filteredGames = [];
      this.allCurrentlyFilteredGames = [];
      this.gamesPageIndex = 0;
      this.currentFilterTerm = '';
      this.activeTextQuery = ''; // Clear text query
      this.isTextSearchActive = false;
      this.tagFilteredGamesHolder = []; // Clear stored tag games
      // Note: The infinite scroll component's 'disabled' state will be reset
      // when it's re-rendered or we can explicitly get a ViewChild to it if needed.
      // For now, relying on *ngIf to remove/re-add it should reset its state.
    }
  }

  dismissModal() { // No data needs to be passed back now
    this.modalController.dismiss();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTagClick(tag: any, categoryName: string) {
    // Set the input field value to the tag name
    this.activeTextQuery = tag.name;

    // Simulate typing the tag into the search input
    const simulatedEvent = {
      target: {
        value: tag.name
      }
    };

    this.onSearchInput(simulatedEvent);
  }

  onSearchInput(event: any) {
    this.activeTextQuery = event.target.value ? event.target.value.trim() : '';
    const searchTermProcess = this.activeTextQuery.toLowerCase();

    if (searchTermProcess.length === 0) {
      if (this.tagFilteredGamesHolder.length > 0) { // A tag was previously selected
        this.allCurrentlyFilteredGames = [...this.tagFilteredGamesHolder];
        this.isTextSearchActive = false;
        this.filteredGames = [];
        this.gamesPageIndex = 0;
        this.loadInitialGamesBatch();
        // currentFilterTerm remains the tag name
      } else { // No tag selected, pure text search cleared
        this.showTagsView();
      }
      return;
    }

    const searchTermLength = this.configService.getConfig()?.SEARCH_TERM_LENGTH ?? 1;

    if (searchTermProcess.length < searchTermLength) {
      this.isTextSearchActive = false; // No longer an active text search for games

      if (this.tagFilteredGamesHolder.length > 0) {
        // A tag is active, and text search within it is now too short.
        // Revert to showing all games for the currently selected tag.
        this.allCurrentlyFilteredGames = [...this.tagFilteredGamesHolder];
        this.filteredGames = [];
        this.gamesPageIndex = 0;
        this.loadInitialGamesBatch();
        // currentFilterTerm remains the tag name. isShowingFilteredGames remains true.
      } else {
        // No tag is active. Text search is too short, but input is not empty.
        // Clear the game list, but keep the typed characters in the search bar.
        this.filteredGames = [];
        this.allCurrentlyFilteredGames = [];
        this.isShowingFilteredGames = false; // Hide the game list section
        // activeTextQuery (bound to search bar value) remains as what the user typed.
        // currentFilterTerm should be empty or irrelevant here.
      }
      return;
    }

    // If searchTermProcess.length >= 3
    this.isTextSearchActive = true;
    this.isShowingFilteredGames = true; // Ensure view is set to show games

    let sourceData = this.tagFilteredGamesHolder.length > 0 ? this.tagFilteredGamesHolder : this.allGamesCategoryData;

    if (!sourceData && (!this.allGamesCategoryData || this.allGamesCategoryData.length === 0)) {
      console.warn('No data available to search from.');
      this.allCurrentlyFilteredGames = [];
      this.filteredGames = [];
      return;
    }
    // Ensure sourceData is an array before filtering
    sourceData = Array.isArray(sourceData) ? sourceData : [];

    // First, get games that match by name
    const nameMatches = sourceData.filter(game =>
      game && game.name && game.name.toLowerCase().includes(searchTermProcess)
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Then, get games that match by tags but NOT by name (to avoid duplicates)
    const tagMatches = sourceData.filter(game =>
      game &&
      (!game.name || !game.name.toLowerCase().includes(searchTermProcess)) && // Exclude name matches
      Array.isArray(game.tags) &&
      game.tags.some(gameTag =>
        typeof gameTag === 'string' && gameTag.toLowerCase().includes(searchTermProcess)
      )
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Combine results: name matches first, then tag matches
    this.allCurrentlyFilteredGames = [...nameMatches, ...tagMatches];

    this.filteredGames = [];
    this.gamesPageIndex = 0;
    this.loadInitialGamesBatch();
  }

  launchGameFromSearch(game: any): void {
    this.control.goToDefaultCategory();
       if(game.is_demoPlay){
        this.navigateToGameDetails(game);
      } else {
    setTimeout(() => {
      this.gameTileService.launchGame(game);
      this.dismissModal();
    }, 300);
  }



  }

  navigateToGameDetails(gameData) {
    this.dismissModal();
    this.navigator.navigateTo('GameDetailsComponent', { multipleModals: false }, { gamedata: gameData });
  }

  // Added method to construct image URL
  getGameImageUrl(game: any): string {
    if (game && game.imagename) {
      const cdnBaseUrl = this.configService.getConfig()?.CDN_BASEURL;
      if (cdnBaseUrl) {
        return `${cdnBaseUrl}/assets/social/games/${game.imagename}`;
      }
    }
    // Return a placeholder or empty string if no image name or CDN_BASEURL is not available
    // Consider using a global placeholder image path from config if available
    return this.gameTileService.tileDefaultImage;
  }

  handleImageError(event: any): void {
    const imgElement = event.target;
    // Prevent infinite loop by checking if we're already showing the fallback
    if (!imgElement.src.includes('failed-img.png')) {
      imgElement.src = '/assets/images/failed-img.png';
    }
  }

  get isMobile(): boolean {
    return !this.utils.getPlatformInfo().isDesktopWeb;
  }
  isFavorite(game){
    return this.favoritesService.isFavorite(game.id);
  }

  toggleFavourite(game: any, event: Event) {
    event.stopPropagation();
    if (!this.userSession.isAuthenticated()) {
      return this.navigator.navigateTo('LoginComponent');
    }
    if (this.favoritesService.isFavorite(game.id)) {
      this.favoritesService.removeFavourites(game.id).subscribe();
      game.isFavourite = false;
    } else {
      this.favoritesService.addToFavorites(game.id).subscribe();
      game.isFavourite = true;
    }
  }
}
